import React, { useMemo, useState } from 'react';

// Components
import TextField from '@material-ui/core/TextField';
import { Spacer, Button, Loader } from 'components';

// Routes
import { useRouter } from 'next/router';

// Validation
import { useFormik } from 'formik';
import * as yup from 'yup';

// Utils
import { awaitAtLeast } from 'utils/async';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { Routes } from 'utils/routes';
import getConfig from 'next/config';
import { writeOrderDetailsInLocalStorage } from 'utils/orders';
import { v4 as uuidv4 } from 'uuid';
import useAuthentication from 'utils/useAuthentication';
import { useSelector } from 'react-redux';
// API
import { submitOrder } from 'api/orders';

const validationSchema = yup.object({
    firstName: yup.string('Indtast dit fornavn').required('Fornavn er krævet'),
    lastName: yup.string('Indtast dit efternavn').required('Efternavn er krævet'),
    email: yup.string('Indtast din e-mail adresse').email('Indtast en gyldig e-mail adresse').required('E-mail adresse er krævet'),
    phone: yup.string('Indtast dit telefonnummer').required('Telefonnummer er krævet'),
    accepted: yup.boolean().oneOf([true], 'Du skal acceptere at dele dine oplysninger med os.'),
});

export default function investeringOrder({ product, discountCode }) {
    const calendlyBookingId = useMemo(uuidv4, []);
    const router = useRouter();
    const [loading, setLoading] = useState(false);
    // Misc
    const { trackEvent } = useMatomo();
    const { publicRuntimeConfig } = getConfig();
    const { authenticated } = useAuthentication();
    const user = useSelector((state) => state.user);
    const vendor = publicRuntimeConfig.advice?.investment?.vendor || 'TealAdvice';
    const formik = useFormik({
        initialValues: { firstName: '', lastName: '', email: '', phone: '', accepted: false },
        validationSchema,
        onSubmit: async ({ firstName, lastName, email, phone }) => {
            setLoading(true);
            try {
                const customer = { firstName, lastName, email, phone };
                const orderToCreate = {
                    disableCalendlyLookup: true,
                    vendor,
                    items: [{ product }],
                    calendlyBookingId,
                    customer,
                    sendEmail: true,
                    discountCode,
                    dataCollectionProgress: {
                        currentStep:3,
                        numberOfSteps:4,
                    },
                };
                const createdOrder = await awaitAtLeast(3000, () => submitOrder(orderToCreate));
                const orderDetails = {
                    order: createdOrder,
                    product,
                    customer,
                    calendlyBookingId,
                };
                writeOrderDetailsInLocalStorage(orderDetails);
                trackEvent({ category: 'affiliates', action: `order_unauthenticated_${product.affiliateId}_${product.productId}` });
                router.push(Routes.Advisory.Nedsparing.Receipt);
                setLoading(false);
            } catch (error) {
                setLoading(false);
            }
        },
    });
    const handleAuthenticatedOrder = async () => {
        setLoading(true);
        try {
            const customer = { ...user, phone: user.data.basic?.phone };
            const orderToCreate = {
                vendor,
                items: [{ product }],
                calendlyBookingId,
                customer,
                endEmail: true,
                discountCode,
                disableCalendlyLookup: true,
                dataCollectionProgress: {
                    currentStep:2,
                    numberOfSteps:4,
                },
            };
            const createdOrder = await awaitAtLeast(3000, () => submitOrder(orderToCreate));
            const orderDetails = {
                order: createdOrder,
                product,
                customer,
                calendlyBookingId,
            };
            writeOrderDetailsInLocalStorage(orderDetails);
            trackEvent({ category: 'affiliates', action: `order_authenticated_${product.affiliateId}_${product.productId}` });
            router.push(Routes.Advisory.Nedsparing.Receipt);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };
    return authenticated ? (
        <section className="contactForm">
            <div>
                <ol>
                    <li>Bestil investeringsrådgivning ved at klikke på knappen ‘Bestil nu’.</li>
                    <li>Book første møde direkte i rådgiverens kalender.</li>
                    <li>
                        Svar på en række spørgsmål og del svarene med rådgiveren, så rådgiveren kan forberede sig grundigt til mødet. Det tager under 10 minutter at svare. Har du ikke tid lige nu?
                        Intet problem. Vi husker dig på det inden mødet.
                    </li>
                </ol>
                <Button onClick={ handleAuthenticatedOrder }>Bestil nu</Button>
            </div>
            <Loader show={ loading } waitText="Vent venligst mens vi færdiggør din bestilling" />
        </section>
    ) : (
        <section className="contactForm">
            <form onSubmit={ formik.handleSubmit } id="contact">
                <div>
                    <div className="formgroups">
                        <div className="formgroup">
                            <TextField
                                label="Fornavn"
                                name="firstName"
                                variant="filled"
                                value={ formik.values.firstName }
                                onChange={ formik.handleChange }
                                onBlur={ formik.handleBlur }
                                error={ formik.touched.firstName && Boolean(formik.errors.firstName) }
                                helperText={ formik.touched.firstName && formik.errors.firstName }
                            />
                        </div>
                        <div className="formgroup">
                            <TextField
                                label="Efternavn"
                                name="lastName"
                                variant="filled"
                                value={ formik.values.lastName }
                                onChange={ formik.handleChange }
                                onBlur={ formik.handleBlur }
                                error={ formik.touched.lastName && Boolean(formik.errors.lastName) }
                                helperText={ formik.touched.lastName && formik.errors.lastName }
                            />
                        </div>
                    </div>
                    <div className="formgroups">
                        <div className="formgroup">
                            <TextField
                                label="E-mailadresse"
                                name="email"
                                variant="filled"
                                value={ formik.values.email }
                                onChange={ formik.handleChange }
                                onBlur={ formik.handleBlur }
                                error={ formik.touched.email && Boolean(formik.errors.email) }
                                helperText={ formik.touched.email && formik.errors.email }
                            />
                        </div>
                        <div className="formgroup">
                            <TextField
                                label="Telefon"
                                name="phone"
                                variant="filled"
                                value={ formik.values.phone }
                                onChange={ formik.handleChange }
                                onBlur={ formik.handleBlur }
                                error={ formik.touched.phone && Boolean(formik.errors.phone) }
                                helperText={ formik.touched.phone && formik.errors.phone }
                            />
                        </div>
                    </div>

                    <Spacer />

                    <div className="formgroups">
                        <div className="formgroup">
                            <div className="checkbox" style={{ margin: 0 }}>
                                <input name="accepted" id="accepted" type="checkbox" value={ formik.values.accepted } onChange={ formik.handleChange } />
                                <label htmlFor="accepted">Jeg bekræfter, at Penly må kontakte mig på e-mail og telefon angående min bestilling.</label>
                            </div>
                        </div>
                    </div>
                    { formik.touched.accepted && <p className="validationError">{ formik.errors.accepted }</p> }
                    <Spacer />
                    <div>
                        <Button type="submit">Bestil nu</Button>
                    </div>
                </div>
            </form>
            <Loader show={ loading } waitText="Vent venligst mens vi færdiggør din bestilling" />
        </section>
    );
}
